// Keyboard and screen reader support for AMP Back in Stock's waitlist popups.
// AMP builds one iframe per popover, all with id="BIS_frame", and opens/closes each by setting
// its inline display. This keeps focus inside the open popup, makes its close link reachable,
// closes it on Escape, makes the page behind it inert, and returns focus to the opener on close.
(() => {
  if (window.waitlistPopupA11y) return;
  window.waitlistPopupA11y = true;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const CONTROL = 'a[href], button, [role="button"], input, select, textarea, [tabindex]';
  const watched = new WeakSet();
  let lastClick = null;
  let opener = null;
  let openFrame = null;
  let inerted = [];

  const isOpen = (frame) => frame.isConnected && getComputedStyle(frame).display !== 'none';
  const isVisible = (element) => element.getClientRects().length > 0 &&
    element.ownerDocument.defaultView.getComputedStyle(element).visibility !== 'hidden';
  const focusables = (doc) => [...doc.querySelectorAll(FOCUSABLE)].filter(isVisible);

  // The control a person used to open the popup: the last trusted click (mouse, Enter, Space),
  // read through shadow DOM for the collection quick add. Script clicks are ignored:
  // <waitlist-button> forwards one to #BIS_trigger, and quick add clicks a temporary .BIS_trigger.
  document.addEventListener('click', (event) => {
    if (!event.isTrusted) return;
    const target = event.composedPath()[0];
    if (!(target instanceof Element)) return;
    lastClick = { element: target.closest(CONTROL) || target, time: performance.now() };
  }, true);

  // The nearest visible control around an element, crossing shadow roots; e.g. the product
  // card's link when a quick-add panel has closed.
  function nearestVisibleControl(element) {
    for (let node = element; node;) {
      const scope = node.parentElement || node.getRootNode().host;
      if (!scope) return null;
      const candidate = [...scope.querySelectorAll(FOCUSABLE)].find(isVisible);
      if (candidate) return candidate;
      node = scope;
    }
    return null;
  }

  function onKeydown(event) {
    const doc = event.currentTarget;
    const close = doc.querySelector('.action-close');

    if (event.key === 'Escape') {
      event.preventDefault();
      close?.click();
      return;
    }

    if ((event.key === 'Enter' || event.key === ' ') && close && event.target === close) {
      event.preventDefault();
      close.click();
      return;
    }

    if (event.key !== 'Tab') return;
    const items = focusables(doc);
    if (!items.length) return;
    const index = items.indexOf(doc.activeElement);
    if (event.shiftKey && index <= 0) {
      event.preventDefault();
      items[items.length - 1].focus();
    } else if (!event.shiftKey && (index === -1 || index === items.length - 1)) {
      event.preventDefault();
      items[0].focus();
    }
  }

  // AMP can rewrite the iframe document between opens, so this runs on every open; the
  // listener is a single function, so adding it again is a no-op.
  function prepare(frame) {
    const doc = frame.contentDocument;
    if (!doc?.querySelector('[role="dialog"]')) return false;

    const close = doc.querySelector('.action-close');
    if (close && !close.hasAttribute('tabindex')) close.setAttribute('tabindex', '0');
    doc.querySelector('[role="dialog"]').setAttribute('aria-modal', 'true');
    doc.addEventListener('keydown', onKeydown);

    if (document.activeElement !== frame || !focusables(doc).includes(doc.activeElement)) {
      focusables(doc)[0]?.focus();
    }
    return true;
  }

  function onOpen(frame) {
    openFrame = frame;
    // AMP can take a few seconds to build a popup the first time it opens.
    opener = lastClick && performance.now() - lastClick.time < 10000 ? lastClick.element : null;
    lastClick = null;
    if (!frame.hasAttribute('title')) {
      frame.setAttribute('title', window.variantStrings?.joinWaitlist || 'Join waitlist');
    }

    inerted = [...document.body.children].filter((element) =>
      !element.contains(frame) && !element.inert && element.tagName !== 'SCRIPT'
    );
    inerted.forEach((element) => { element.inert = true; });

    // AMP may still be writing the iframe when it shows it; retry briefly.
    let tries = 0;
    const attempt = () => {
      if (openFrame !== frame || prepare(frame)) return;
      if (++tries < 20) setTimeout(attempt, 50);
    };
    attempt();
  }

  function onClose(frame) {
    if (openFrame !== frame) return;
    openFrame = null;
    inerted.forEach((element) => { element.inert = false; });
    inerted = [];

    // Only restore focus if it is still on the closed popup (or nowhere).
    const active = document.activeElement;
    if (active !== frame && active !== document.body && active !== null) return;

    const target = opener?.isConnected && isVisible(opener) ? opener : opener && nearestVisibleControl(opener);
    target?.focus();
    opener = null;
  }

  function watch(frame) {
    if (watched.has(frame)) return;
    watched.add(frame);
    new MutationObserver(() => {
      if (isOpen(frame)) {
        if (openFrame !== frame) onOpen(frame);
      } else {
        onClose(frame);
      }
    }).observe(frame, { attributes: true, attributeFilter: ['style'] });
    if (isOpen(frame)) onOpen(frame);
  }

  const scan = () => document.querySelectorAll('iframe#BIS_frame').forEach(watch);
  new MutationObserver(scan).observe(document.body, { childList: true });
  scan();
})();
